import { logUsage } from "owa-analytics";
import { getApplicationSettings } from "owa-application-settings";
import { assertNever } from "owa-assert";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isConsumer from "owa-session-store/lib/utils/isConsumer";
import { getAccountScopeUserSettings } from "owa-session-store/lib/selectors/getAccountScopeUserSettings";
import { tryGetGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isOcpsStoreReady } from "owa-ocps-get-policies";
import { isOcpsPolicyEnabled, OcpsPolicy } from "owa-ocps-policy-store";
import { getBrowserInfo, getOs, isMinimumBrowserVersion, type Browser, type OperatingSystem } from "owa-user-agent";
import getAttachmentPolicy from "owa-session-store/lib/utils/getAttachmentPolicy";
import type { FluidOwaSource } from "../enums/FluidEnums";
import { isCollabSpaceEnabledForSource } from "./isCollabSpaceEnabledForSource";
import type { MailboxInfo } from "owa-client-types";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getOneDriveForBusinessAvailability } from "./getOneDriveForBusinessAvailability";
import { isOCPSPolicyEnabledForLoopType } from "./isOCPSPolicyEnabledForLoopType";
import { LoopType } from "./constants";
import { isServiceRequestSupportedForMailbox } from "owa-service/lib/utils/isServiceRequestSupportedForMailbox";

const loopRegistrationIds: Partial<Record<LoopType, string>> = {
    [LoopType.Poll]: "@ms/forms-loop",
    [LoopType.VideoRecord]: "@fluidx/video",
};

// Legacy callers share one entry; opt-in callers keep account/source/type/preload histories.
const fluidEnabledStatuses = new Map<string, {
    isEnabled: boolean;
    reason: string | undefined;
    isOcpsReady: boolean | undefined;
}>();

interface FluidEligibility {
    isEnabled: boolean;
    disabledReasonInternal: string | undefined;
    hasAttachmentProvider: boolean | undefined;
    hasBPOSNavBarData: boolean;
    isMailboxSettingsResolved: boolean;
    mailboxInfoForConsumerCheck: MailboxInfo;
    fallbackMailboxInfoSelected: boolean;
}

/** Evaluates current eligibility without logging or advancing telemetry history. */
export function evaluateFluidEnabledForSource(source: FluidOwaSource, mailboxInfo: MailboxInfo, loopType: LoopType, skipCheckingUserOptions: boolean = false, useAccountSpecificNavBarData: boolean = false): FluidEligibility {
    const { hasAttachmentProvider, hasBPOSNavBarData, hasOneDriveForBusiness } = getOneDriveForBusinessAvailability(mailboxInfo, useAccountSpecificNavBarData);
    const checkOneDriveForBusiness = loopType === LoopType.Fluid || loopType === LoopType.VideoRecord;
    const isChapterThreeLoop = loopType !== LoopType.AnyLoop && loopType !== LoopType.Fluid;
    const registrationId = loopRegistrationIds[loopType];
    
    let disabledReasonInternal = undefined;
    
    // once Fluid is supported in consumer, we want to continue restricting feature access to Cloud Cache users
    // therefore replace isConsumer() with getUserConfiguration().SessionSettings?.IsShadowMailbox
    // For preload we check if fluid is enabled before options are loaded.
    // Fall back to global settings account for cross-tenant contexts (e.g. GDAP) where
    // the mailboxInfo can't be resolved in the session store.
    const isMailboxSettingsResolved = !!getAccountScopeUserSettings(mailboxInfo)?.SessionSettings;
    let mailboxInfoForConsumerCheck = mailboxInfo;
    let fallbackMailboxInfoSelected = false;
    if (!isMailboxSettingsResolved) {
        const globalMailboxInfo = tryGetGlobalSettingsAccountMailboxInfo(false);
        if (globalMailboxInfo) {
            mailboxInfoForConsumerCheck = globalMailboxInfo;
            fallbackMailboxInfoSelected = true;
        }
    }
    if (!isServiceRequestSupportedForMailbox(mailboxInfo)) {
        disabledReasonInternal = "ServiceRequestNotSupportedForMailbox";
    }
    else if (isConsumer(undefined /* smtpAddress */, mailboxInfoForConsumerCheck)) {
        disabledReasonInternal = "Consumer";
    }
    else if (!hasOneDriveForBusiness && checkOneDriveForBusiness) {
        disabledReasonInternal = "NoOneDriveForBusiness";
    }
    else if (!isBrowserSupported()) {
        disabledReasonInternal = "BrowserNotSupported";
    }
    else if (!(isOCPSPolicyEnabledForLoopType(loopType, mailboxInfo) || skipCheckingUserOptions)) {
        if (isOcpsStoreReady()) {
            disabledReasonInternal = "OcpsPolicyDisabled";
        }
        else {
            disabledReasonInternal = "OcpsPolicyNotReady";
        }
    }
    else if (isChapterThreeLoop && !isGlobalFeatureEnabled("cmp-fluid-chapterThree")) {
        disabledReasonInternal = "ChapterThreeFlightDisabled";
    }
    else if (registrationId &&
        !getApplicationSettings("Chapter3AllowList").registrationIds.includes(registrationId)) {
        disabledReasonInternal = "LoopTypeNotInAllowList";
    }
    
    if (!disabledReasonInternal && !isFluidSourceFlightEnabled(source, mailboxInfo)) {
        disabledReasonInternal = "flightCheck";
    }
    return {
        isEnabled: !disabledReasonInternal,
        disabledReasonInternal,
        hasAttachmentProvider,
        hasBPOSNavBarData,
        isMailboxSettingsResolved,
        mailboxInfoForConsumerCheck,
        fallbackMailboxInfoSelected,
    };
}

/**
 * @param source Calling surface; determines source-specific flight checks.
 * @param mailboxInfo Mailbox providing context for capability and policy checks.
 * @param loopType Component type; determines OneDrive and OCPS requirements.
 * @param skipCheckingUserOptions Bypass OCPS blocking for preload checks (default false).
 * @param useAccountSpecificNavBarData Use mailbox-scoped navbar data for non-global accounts
 * instead of the global cache (default false).
 * @param logInitialStateAndChangesOnly Log initial results and eligibility/reason/readiness changes
 * per account/source/type/preload, suppressing unchanged diagnostics instead of using legacy logging
 * (default false).
 */
export function isFluidEnabledForSource(source: FluidOwaSource, mailboxInfo: MailboxInfo, loopType: LoopType, skipCheckingUserOptions: boolean = false, useAccountSpecificNavBarData: boolean = false, logInitialStateAndChangesOnly: boolean = false): boolean {
    const { isEnabled, disabledReasonInternal, hasAttachmentProvider, hasBPOSNavBarData, isMailboxSettingsResolved, mailboxInfoForConsumerCheck, fallbackMailboxInfoSelected, } = evaluateFluidEnabledForSource(source, mailboxInfo, loopType, skipCheckingUserOptions, useAccountSpecificNavBarData);
    const key = logInitialStateAndChangesOnly
        ? JSON.stringify([
            getAccountKeyForMailboxInfo(mailboxInfo),
            source,
            loopType,
            skipCheckingUserOptions,
        ])
        : "legacy";
    const previous = fluidEnabledStatuses.get(key);
    const isOcpsReady = logInitialStateAndChangesOnly ? isOcpsStoreReady() : undefined;
    const shouldLogStatus = previous
        ? previous.isEnabled !== isEnabled ||
            previous.reason !== disabledReasonInternal ||
            (logInitialStateAndChangesOnly && previous.isOcpsReady !== isOcpsReady)
        : logInitialStateAndChangesOnly;
    if (logInitialStateAndChangesOnly && !shouldLogStatus) {
        return isEnabled;
    }
    if (hasBPOSNavBarData !== hasAttachmentProvider) {
        logUsage("FluidEnabledCheckNavBarDataMismatch", mailboxInfo, {
            hasNavBarData: hasBPOSNavBarData,
            hasAttachmentProvider,
        });
    }
    if (disabledReasonInternal && disabledReasonInternal !== "flightCheck") {
        if (!getAttachmentPolicy(mailboxInfo)) {
            logUsage("FluidEnabledAttachmentPolicyUndefined", mailboxInfo, { source });
        }
        logUsage("FluidNotEnabled", mailboxInfo, {
            source,
            isAttachmentPolicyEnabled: hasAttachmentProvider,
            isBrowserSupported: isBrowserSupported(),
            browser: getBrowserInfo().browser,
            isOcpsPolicyEnabled: isOcpsPolicyEnabled(OcpsPolicy.LoopEnabledForOutlook, true, mailboxInfo) &&
                isOcpsPolicyEnabled(OcpsPolicy.Loop, true, mailboxInfo),
            isOcpsReady: isOcpsStoreReady(),
            isConsumer: isConsumer(undefined /* smtpAddress */, mailboxInfoForConsumerCheck),
            usedFallbackMailboxInfo: !isMailboxSettingsResolved,
            hasBPOSNavBarData,
            loopType,
            disabledReasonInternal,
            isPreload: skipCheckingUserOptions,
            ...getMeetingNotesDiagnostics(source, loopType, disabledReasonInternal, fallbackMailboxInfoSelected),
        });
    }
    else if (disabledReasonInternal === "flightCheck" &&
        (source === "MailReadingPane" ||
            source === "MailCompose" ||
            source === "CalendarEventBody" ||
            source === "ForwardEventEditor" ||
            source === "MeetingBriefing" ||
            source === "None")) {
        logUsage("FluidNotEnabledFromFlightCheck", mailboxInfo, { source });
    }
    if (shouldLogStatus) {
        logUsage("FluidEnabledStatus", mailboxInfo, {
            schemaVersion: 2,
            isInitialCheck: previous === undefined,
            isNowEnabled: isEnabled,
            wasPreviouslyEnabled: previous?.isEnabled,
            newDisabledReason: disabledReasonInternal,
            previousDisabledReason: previous?.reason,
            isOcpsReady,
            isPreload: skipCheckingUserOptions,
            source,
            loopType,
            usedFallbackMailboxInfo: !isMailboxSettingsResolved,
            ...getMeetingNotesDiagnostics(source, loopType, disabledReasonInternal, fallbackMailboxInfoSelected),
        });
    }
    fluidEnabledStatuses.set(key, { isEnabled, reason: disabledReasonInternal, isOcpsReady });
    return isEnabled;
}

function isFluidSourceFlightEnabled(source: FluidOwaSource, mailboxInfo: MailboxInfo): boolean {
    switch (source) {
        case "MailReadingPane":
        case "MailCompose":
        case "CalendarEventBody":
        case "ForwardEventEditor":
        // Meeting Briefing is gated upstream by its own cal-meetingBriefing* flights, so it only
        // needs the generic Loop check here. It is deliberately not grouped with CalendarCompose,
        // which is additionally gated on meeting-notes flags that do not apply to briefings.
        case "MeetingBriefing":
        case "None":
            return isGlobalFeatureEnabled("cmp-prague");
        case "CalendarReadingPane":
        case "MailCalendarCard":
        case "CalendarCompose":
            return isCollabSpaceEnabledForSource(source, mailboxInfo);
        case "MOS":
            return isGlobalFeatureEnabled("mos-hoopApp");
        case "NewsletterCompose":
        case "NewsletterPreview":
            return isGlobalFeatureEnabled("addison-loops");
        default:
            return assertNever(source);
    }
}

function isBrowserSupported(): boolean {
    const browser: Browser = getBrowserInfo().browser;
    switch (browser) {
        case "Chrome":
        case "Edge Anaheim":
            return isMinimumBrowserVersion([120]);
        case "Firefox":
            return isMinimumBrowserVersion([115]);
        case "Opera":
            return true;
        case "Safari":
            return getOs() !== "Windows" && isMinimumBrowserVersion([16, 0]);
        default:
            return false;
    }
}

function getMeetingNotesDiagnostics(source: FluidOwaSource, loopType: LoopType, reason: string | undefined, fallbackMailboxInfoSelected: boolean) {
    if ((source === "CalendarCompose" || source === "CalendarReadingPane") &&
        loopType === LoopType.Fluid &&
        isGlobalFeatureEnabled("cal-fluid-meetingNotesDiagnostics")) {
        return {
            evaluatedGate: getEligibilityGate(reason),
            fallbackMailboxInfoSelected,
        };
    }
    return undefined;
}

function getEligibilityGate(reason: string | undefined) {
    switch (reason) {
        case undefined:
            return "None";
        case "ServiceRequestNotSupportedForMailbox":
            return "ServiceRequest";
        case "Consumer":
            return "Consumer";
        case "NoOneDriveForBusiness":
        case "OneDriveDataNotReady":
            return "OneDrive";
        case "BrowserNotSupported":
            return "Browser";
        case "OcpsPolicyDisabled":
        case "OcpsPolicyNotReady":
            return "Ocps";
        case "flightCheck":
            return "SourceFlight";
        default:
            return "Unknown";
    }
}
